import { fetchApi } from './api';

const BASE = 'network-replenishment';

export type ApprovalMode = 'AUTOMATIC' | 'MANUAL';
export type AllocationMethod = 'PRIORITY' | 'PROPORTIONAL' | 'CRITICAL_FIRST';
export type ReplenishmentCadence = 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
export type PriorityRankMethod = 'SALES_DESC' | 'ALPHA' | 'KEEP';

export interface NetworkNextCycle {
  order_date: string;
  planned_shipping_date: string;
  expected_receipt_date: string;
  next_possible_receipt_date: string;
  schedule_id: number | null;
  preparation_days: number;
  transport_days: number;
  used_lane_defaults: boolean;
}

export interface SupplyLaneSchedule {
  id: number;
  supply_lane: number;
  order_weekday: number;
  cutoff_time: string | null;
  preparation_days: number;
  transport_days: number;
  active_from: string | null;
  active_to: string | null;
  is_active: boolean;
}

export interface SupplyLane {
  id: number;
  organization: number;
  source_warehouse: number;
  source_warehouse_code: string;
  source_warehouse_name?: string;
  destination_warehouse: number;
  destination_warehouse_code: string;
  destination_warehouse_name?: string;
  destination_replenishment_priority?: number;
  preparation_lead_time_days: number;
  transport_lead_time_days: number;
  priority: number;
  cadence: ReplenishmentCadence;
  biweekly_anchor_date: string | null;
  monthly_weekday_ordinal: number | null;
  monthly_weekday: number | null;
  is_active: boolean;
  schedule_is_custom?: boolean;
  schedules: SupplyLaneSchedule[];
  next_cycle: NetworkNextCycle | null;
}

export interface NetworkReplenishmentConfig {
  id: number;
  organization: number;
  default_source_warehouse: number;
  default_source_warehouse_code: string;
  approval_mode: ApprovalMode;
  automatic_approval_time: string | null;
  auto_send_to_erp: boolean;
  block_when_source_stock_is_unknown: boolean;
  allow_partial_allocation: boolean;
  allow_negative_source_stock: boolean;
  allocation_method: AllocationMethod;
  create_zero_quantity_lines: boolean;
  extra_config: Record<string, unknown>;
  default_cadence?: ReplenishmentCadence;
  default_order_weekdays?: number[];
  default_biweekly_anchor_date?: string | null;
  default_monthly_weekday_ordinal?: number | null;
  default_monthly_weekday?: number | null;
  priority_rank_method?: PriorityRankMethod;
  priority_rank_lookback_days?: number;
  default_schedule_updated_at?: string | null;
  excess_coverage_cycles?: number;
  created_at: string;
  updated_at: string;
}

export interface BulkSchedulePayload {
  cadence: ReplenishmentCadence;
  weekdays?: number[];
  biweekly_anchor_date?: string | null;
  monthly_weekday_ordinal?: number | null;
  monthly_weekday?: number | null;
  rank_method?: PriorityRankMethod;
  lookback_days?: number;
  force_all?: boolean;
  persist_defaults?: boolean;
}

export interface BulkLaneImpact {
  lane_id: number;
  destination_warehouse_id: number;
  destination_code: string;
  destination_name: string;
  schedule_is_custom: boolean;
  will_skip_custom: boolean;
  current_cadence: string;
  current_weekdays: number[];
  current_priority: number;
  current_store_priority: number;
  proposed_cadence: string;
  proposed_weekdays: number[];
  proposed_priority: number | null;
  proposed_store_priority: number | null;
  rank_metric: number | null;
}

export interface BulkScheduleResult {
  force_all: boolean;
  rank_method: PriorityRankMethod;
  lookback_days: number;
  apply_count: number;
  skip_count: number;
  total_count: number;
  updated_count?: number;
  skipped_count?: number;
  lanes: BulkLaneImpact[];
}

export interface WarehouseProductAssortment {
  id: number;
  warehouse: number;
  warehouse_code: string;
  product: number;
  product_code: string;
  is_active: boolean;
}

export interface StockTransferListItem {
  id: number;
  transfer_number: string;
  status: string;
  approval_mode: string;
  source_warehouse: number;
  source_warehouse_code: string;
  destination_warehouse: number;
  destination_warehouse_code: string;
  order_date: string;
  planned_shipping_date: string | null;
  expected_receipt_date: string | null;
  approved_at: string | null;
  items_count: number;
  total_quantity?: string | number;
  /** Valor estimado: suma qty × ProductSupplier.cost (proveedor primario / almacén origen). */
  total_value?: string | number | null;
  notes: string;
  external_ref?: string;
  external_status?: string;
  integration_error?: string;
}

export interface BulkApproveResult {
  requested_transfer_ids: number[];
  destination_warehouse_ids: number[];
  matched_count: number;
  approved_count: number;
  failed_count: number;
  results: Array<{
    id: number;
    transfer_number: string;
    destination_warehouse_id: number;
    ok: boolean;
    status?: string;
    detail?: unknown;
  }>;
}

export interface LineReviewMetrics {
  cycle_days: number;
  days_of_cover_before: string | number | null;
  days_of_cover_after: string | number | null;
  projected_stock: string | number | null;
  exception_badge: 'STOCKOUT' | 'CUT' | 'EXCESS' | 'EDITED' | null;
}

export interface StockTransferItem {
  id: number;
  product: number;
  product_code: string;
  product_name: string;
  unit_code: string;
  requested_quantity: string | number;
  adjusted_quantity: string | number;
  allocated_quantity: string | number;
  unallocated_quantity: string | number;
  approved_quantity: string | number | null;
  effective_quantity: string | number;
  unit_cost?: string | number | null;
  line_value?: string | number | null;
  source_available_stock_snapshot?: string | number;
  destination_stock_snapshot?: string | number;
  destination_stock_in_transit_snapshot?: string | number;
  forecast_protection_period?: string | number;
  target_stock_snapshot?: string | number;
  first_stockout_date?: string | null;
  allocation_reason?: string;
  adjustment_trace?: unknown[];
  explanation: string;
  is_user_edited: boolean;
  review?: LineReviewMetrics;
}

export interface StockTransferEvent {
  id: number;
  event_type: string;
  previous_status: string;
  new_status: string;
  user_username: string | null;
  message: string;
  created_at: string;
}

export interface StockTransferDetail extends StockTransferListItem {
  items: StockTransferItem[];
  events: StockTransferEvent[];
  integration_error: string;
  approved_by: number | null;
  cycle_days?: number;
  next_receipt_date?: string | null;
  excess_coverage_cycles?: number;
}

export interface NetworkReplenishmentDashboardKpis {
  as_of: string;
  transfers_to_review: number;
  transfers_approved_today: number;
  transfers_sent_today: number;
  transfers_with_error: number;
  products_incomplete_allocation: number;
  units_to_transfer: string | number;
  stores_at_stockout_risk: number;
  central_committed_stock: string | number;
  upcoming_shipments: number;
}

type Paginated<T> = { results: T[]; count: number };

export const networkReplenishmentService = {
  async getDashboardKpis(): Promise<NetworkReplenishmentDashboardKpis> {
    return fetchApi<NetworkReplenishmentDashboardKpis>(`${BASE}/dashboard/`);
  },

  async getConfig(): Promise<NetworkReplenishmentConfig | null> {
    return fetchApi<NetworkReplenishmentConfig | null>(`${BASE}/config/`);
  },

  async saveConfig(
    data: Partial<NetworkReplenishmentConfig> & { default_source_warehouse: number },
  ): Promise<NetworkReplenishmentConfig> {
    return fetchApi<NetworkReplenishmentConfig>(`${BASE}/config/`, {
      method: 'POST',
      body: data,
    });
  },

  async getSupplyLanes(params?: { is_active?: boolean }): Promise<SupplyLane[]> {
    const response = await fetchApi<Paginated<SupplyLane>>(`${BASE}/supply-lanes/`, { params });
    return response.results || [];
  },

  async updateSupplyLane(
    id: number,
    data: Partial<Pick<SupplyLane, 'priority' | 'is_active'>>,
  ): Promise<SupplyLane> {
    return fetchApi<SupplyLane>(`${BASE}/supply-lanes/${id}/`, {
      method: 'PATCH',
      body: data,
    });
  },

  async setLaneOrderWeekdays(id: number, weekdays: number[]): Promise<SupplyLane> {
    return fetchApi<SupplyLane>(`${BASE}/supply-lanes/${id}/set-order-weekdays/`, {
      method: 'POST',
      body: { weekdays },
    });
  },

  async setLaneSchedule(
    id: number,
    body: {
      cadence: ReplenishmentCadence;
      weekdays?: number[];
      biweekly_anchor_date?: string | null;
      monthly_weekday_ordinal?: number | null;
      monthly_weekday?: number | null;
    },
  ): Promise<SupplyLane> {
    return fetchApi<SupplyLane>(`${BASE}/supply-lanes/${id}/set-schedule/`, {
      method: 'POST',
      body,
    });
  },

  async resetLaneScheduleToDefault(id: number): Promise<SupplyLane> {
    return fetchApi<SupplyLane>(`${BASE}/supply-lanes/${id}/reset-schedule-to-default/`, {
      method: 'POST',
      body: {},
    });
  },

  async previewBulkSchedule(body: BulkSchedulePayload): Promise<BulkScheduleResult> {
    return fetchApi<BulkScheduleResult>(`${BASE}/config/preview-bulk-schedule/`, {
      method: 'POST',
      body,
    });
  },

  async applyBulkSchedule(body: BulkSchedulePayload): Promise<BulkScheduleResult> {
    return fetchApi<BulkScheduleResult>(`${BASE}/config/apply-bulk-schedule/`, {
      method: 'POST',
      body,
    });
  },

  async getAssortment(params?: {
    warehouse?: number;
    is_active?: boolean;
    page_size?: number;
  }): Promise<Paginated<WarehouseProductAssortment>> {
    return fetchApi<Paginated<WarehouseProductAssortment>>(`${BASE}/assortment/`, {
      params: { page_size: 20, ...params },
    });
  },

  async getStockTransfers(params?: {
    status?: string;
    destination_warehouse?: number;
    search?: string;
  }): Promise<StockTransferListItem[]> {
    const response = await fetchApi<Paginated<StockTransferListItem>>(`${BASE}/stock-transfers/`, {
      params,
    });
    return response.results || [];
  },

  async getStockTransfer(id: number): Promise<StockTransferDetail> {
    return fetchApi<StockTransferDetail>(`${BASE}/stock-transfers/${id}/`);
  },

  async approveStockTransfer(
    id: number,
    body?: {
      item_quantities?: Record<number, string | number>;
      allow_over_allocation?: boolean;
      notes?: string;
    },
  ): Promise<StockTransferDetail> {
    return fetchApi<StockTransferDetail>(`${BASE}/stock-transfers/${id}/approve/`, {
      method: 'POST',
      body: body || {},
    });
  },

  async bulkApproveStockTransfers(body: {
    transfer_ids?: number[];
    destination_warehouse_ids?: number[];
    approve_all_pending?: boolean;
    allow_over_allocation?: boolean;
    notes?: string;
  }): Promise<BulkApproveResult> {
    return fetchApi<BulkApproveResult>(`${BASE}/stock-transfers/bulk-approve/`, {
      method: 'POST',
      body,
    });
  },

  async cancelStockTransfer(id: number, reason?: string): Promise<StockTransferDetail> {
    return fetchApi<StockTransferDetail>(`${BASE}/stock-transfers/${id}/cancel/`, {
      method: 'POST',
      body: { reason: reason || '' },
    });
  },

  async sendStockTransfer(
    id: number,
    body?: { force_error?: boolean; notes?: string },
  ): Promise<StockTransferDetail> {
    return fetchApi<StockTransferDetail>(`${BASE}/stock-transfers/${id}/send/`, {
      method: 'POST',
      body: body || {},
    });
  },

  async retryStockTransfer(
    id: number,
    body?: { force_error?: boolean },
  ): Promise<StockTransferDetail> {
    return fetchApi<StockTransferDetail>(`${BASE}/stock-transfers/${id}/retry/`, {
      method: 'POST',
      body: body || {},
    });
  },

  async markShippedStockTransfer(
    id: number,
    body?: { shipping_date?: string; notes?: string },
  ): Promise<StockTransferDetail> {
    return fetchApi<StockTransferDetail>(`${BASE}/stock-transfers/${id}/mark-shipped/`, {
      method: 'POST',
      body: body || {},
    });
  },

  async receiveStockTransfer(
    id: number,
    body?: {
      item_quantities?: Record<number, string | number>;
      receipt_date?: string;
      notes?: string;
    },
  ): Promise<StockTransferDetail> {
    return fetchApi<StockTransferDetail>(`${BASE}/stock-transfers/${id}/receive/`, {
      method: 'POST',
      body: body || {},
    });
  },

  async editTransferItem(
    itemId: number,
    approved_quantity: string | number,
    reason?: string,
  ): Promise<StockTransferItem> {
    return fetchApi<StockTransferItem>(`${BASE}/stock-transfer-items/${itemId}/`, {
      method: 'PATCH',
      body: { approved_quantity, reason: reason || '' },
    });
  },
};
